/**
 * Improve the general accessibility
 */
export const a11y = () => {
  /**
   * The prefers-reduced-motion media query detects whether the user has requested the operating system to minimize the amount of animation or motion it uses.
   * @see {@link https://web.dev/articles/prefers-reduced-motion?hl=en}
   */
  const handleReducedMotion = () => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const update = () => {
      const isReduced = mediaQuery.matches;

      if (isReduced) {
        /** Pause autoplay videos */
        const videos: HTMLVideoElement[] = Array.from(
          document.querySelectorAll('video[autoplay]')
        );

        videos.forEach((x) => {
          x.pause();
        });
      }
    };

    /** Listen for reduced motion settings changed on device */
    mediaQuery.addEventListener('change', update.bind(this));

    update();
  };

  /**
   * Scrollable containers (e.g. wide tables) must be reachable via keyboard so their overflowing content can be scrolled with the arrow keys (WCAG 2.1.1).
   * They only become a tab stop while their content actually overflows, to avoid empty tab stops.
   * Markup: `<div role="region" aria-labelledby="…" data-scroll-region data-scroll-hint="id-of-hint-element">`
   * @see {@link https://www.w3.org/WAI/WCAG22/Understanding/keyboard.html}
   */
  const handleScrollableRegions = () => {
    const regions: HTMLElement[] = Array.from(
      document.querySelectorAll('[data-scroll-region]')
    );

    if (!regions.length) return;

    const update = (region: HTMLElement) => {
      const isScrollable =
        region.scrollWidth > region.clientWidth ||
        region.scrollHeight > region.clientHeight;
      const hintId = region.dataset.scrollHint;

      if (isScrollable) {
        region.tabIndex = 0;
        if (hintId) region.setAttribute('aria-describedby', hintId);
      } else {
        region.removeAttribute('tabindex');
        region.removeAttribute('aria-describedby');
      }
    };

    /** Re-check whenever the region or its content changes size (viewport, zoom, fonts, accordion) */
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const region = (<HTMLElement>entry.target).closest<HTMLElement>(
          '[data-scroll-region]'
        );
        if (region) update(region);
      });
    });

    regions.forEach((region) => {
      observer.observe(region);
      Array.from(region.children).forEach((child) => observer.observe(child));
      update(region);
    });
  };

  handleReducedMotion();
  handleScrollableRegions();
};
